import { useEffect, useRef, useState } from "react";
import { AgentEvent } from "../../../api/sseClient";
import logo from "../../../assets/favicon.png";

interface CollabStreamingMessageProps {
  content: string;
  isStreaming: boolean;
  agentEvents?: AgentEvent[];
  heartbeatCounter?: number;
  isStalled?: boolean;
}

export const CollabStreamingMessage = ({
  content,
  isStreaming,
  agentEvents,
  heartbeatCounter,
  isStalled = false,
}: CollabStreamingMessageProps) => {
  const [animKey, setAnimKey] = useState(0);
  const [animClass, setAnimClass] = useState("");
  const lastSeenAgentRef = useRef<{ agentName: string } | undefined>(undefined);

  // Pulse on every heartbeat; reset to idle after 6s of silence.
  useEffect(() => {
    if (!isStreaming || (heartbeatCounter ?? 0) === 0) return;

    setAnimClass("animate-logo-pulse");
    setAnimKey((k) => k + 1);

    const timeout = setTimeout(() => {
      setAnimClass("");
    }, 6000);

    return () => clearTimeout(timeout);
  }, [heartbeatCounter, isStreaming]);

  // Turn off animation when the stream ends (still image)
  useEffect(() => {
    if (!isStreaming) {
      lastSeenAgentRef.current = undefined;
      setAnimClass("");
      setAnimKey(0);
    }
  }, [isStreaming]);

  if (!content && !isStreaming) return null;

  const events = agentEvents ?? [];

  const toolCalls = events.flatMap((e, i) =>
    e.type === "tool_call" ? [{ ...e.toolEvent, eventIndex: i }] : [],
  );

  const completedIds = new Set(
    events.flatMap((e) =>
      e.type === "tool_result" && e.toolEvent.toolCallId !== null
        ? [e.toolEvent.toolCallId]
        : [],
    ),
  );

  const agentCalls = events.flatMap((e) =>
    e.type === "agent_call" ? [e.agentCall] : [],
  );

  const visibleCalls = toolCalls.slice(-3);
  const latestAgent = agentCalls.at(-1);

  if (latestAgent) lastSeenAgentRef.current = latestAgent;

  // The latest report is the drafting's state: its start, or its end. The end
  // names only the columns whose test landed, so the lines come from it alone;
  // an empty end leaves no "Drafted" line.
  const drafting = events
    .flatMap((e) => (e.type === "drafting_progress" ? [e.drafting] : []))
    .at(-1);
  const isDrafting = drafting !== undefined && !drafting.done;

  const hasActiveTool =
    isDrafting ||
    visibleCalls.some(
      (tc) => tc.toolCallId === null || !completedIds.has(tc.toolCallId),
    );

  const showThinking =
    isStreaming && (heartbeatCounter ?? 0) > 0 && !hasActiveTool;

  return (
    <div className="flex justify-start">
      <div className="max-w-[85%] rounded-lg px-4 py-3 bg-stone-100 text-stone-900">
        <div className="text-sm whitespace-pre-wrap break-words">
          {content}
          {isStreaming && (
            <div
              className={`flex items-center gap-1.5${content ? " mt-2" : ""}`}
            >
              <div className="w-4 h-4 flex-shrink-0">
                <img
                  key={animKey}
                  src={logo}
                  alt=""
                  className={`w-4 h-4 object-contain ${animClass || "animate-logo-idle"}`}
                />
              </div>
              {lastSeenAgentRef.current && (
                <span className="text-xs text-stone-400/70 italic">
                  → {lastSeenAgentRef.current.agentName}
                </span>
              )}
            </div>
          )}
          {isStreaming && visibleCalls.length > 0 && (
            <div
              className={`space-y-1${content || isStreaming ? " mt-1" : ""}`}
            >
              {visibleCalls.map((tc) => {
                const isDone =
                  tc.toolCallId !== null && completedIds.has(tc.toolCallId);
                return (
                  <div
                    key={tc.eventIndex}
                    className="flex items-center gap-1.5"
                  >
                    <span
                      className={`w-1.5 h-1.5 rounded-full flex-shrink-0 transition-colors duration-500 ${
                        isDone
                          ? "bg-green-400/70"
                          : "bg-stone-400 animate-pulse"
                      }`}
                    />
                    <span
                      className={`text-xs truncate transition-colors duration-500 ${
                        isDone ? "text-stone-400" : "text-stone-500"
                      }`}
                    >
                      {tc.displayName}
                      {tc.documentName ? ` · ${tc.documentName}` : ""}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
          {isStreaming && drafting && drafting.columnNames.length > 0 && (
            <div className="space-y-1 mt-1">
              {/* Names are unique per table only, so two tables may share one. */}
              {drafting.columnNames.map((columnName, index) => (
                <div
                  key={`${index}-${columnName}`}
                  className="flex items-center gap-1.5"
                >
                  <span
                    className={`w-1.5 h-1.5 rounded-full flex-shrink-0 transition-colors duration-500 ${
                      drafting.done
                        ? "bg-green-400/70"
                        : "bg-stone-400 animate-pulse"
                    }`}
                  />
                  <span
                    className={`text-xs truncate transition-colors duration-500 ${
                      drafting.done ? "text-stone-400" : "text-stone-500"
                    }`}
                  >
                    {drafting.done ? "Drafted" : "Drafting"} analytics test:{" "}
                    {columnName}
                  </span>
                </div>
              ))}
            </div>
          )}
          {showThinking && (
            <span
              className={`block text-xs italic${
                content || isStreaming || visibleCalls.length > 0 ? " mt-1" : ""
              } ${isStalled ? "text-amber-500" : "text-stone-400"}`}
            >
              {isStalled ? "Still working..." : "Thinking..."}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};
